// ── Shared chart configuration for study presentation pages ───────────────

export const colorPalette: string[] = [
  '#11a0a5',
  '#22d4dc',
  '#ffd166',
  '#E8634A',
  '#38bdf8',
  '#a78bfa',
  '#84cc16',
]

export const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#091c1e',
      borderColor: 'rgba(255,255,255,.1)',
      borderWidth: 1,
      titleColor: '#fff',
      bodyColor: 'rgba(255,255,255,.64)',
      padding: 9,
      cornerRadius: 7,
    },
  },
  scales: {
    x: {
      grid: { color: 'rgba(255,255,255,.07)' },
      ticks: { color: 'rgba(255,255,255,.52)', font: { size: 9 } },
    },
    y: {
      grid: { color: 'rgba(255,255,255,.07)' },
      ticks: { color: 'rgba(255,255,255,.52)', font: { size: 9 } },
    },
  },
} as const

export type RgbTriple = [number, number, number]

/** `#rrggbb` (or `#rgb`) to an RGB triple. Falls back to mid-grey on anything unparseable, so a
 *  missing or malformed token degrades to a visible colour rather than `NaN` in an `rgb()`. */
function parseHexTriple(value: string): RgbTriple {
  let hex = value.trim().replace(/^#/, '')
  if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c)
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return [128, 128, 128]
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ]
}

/** Axis, legend, and tooltip colors from `.study-presentation` CSS variables (`light_theme` sets light values). */
export function getStudyChartThemeColors(host: HTMLElement): {
  tick: string
  grid: string
  crosshair: string
  legend: string
  legendStrong: string
  /** Diverging ramp stops as RGB triples, low → middle → high. */
  divergingStops: [RgbTriple, RgbTriple, RgbTriple]
  tooltipBg: string
  tooltipTitle: string
  tooltipBody: string
  tooltipBorder: string
} {
  const s = getComputedStyle(host)
  const g = (name: string, fallback: string) => s.getPropertyValue(name).trim() || fallback
  return {
    tick: g('--chart-tick', 'rgba(255,255,255,.52)'),
    grid: g('--chart-grid', 'rgba(255,255,255,.07)'),
    crosshair: g('--chart-crosshair', 'rgba(255,255,255,.28)'),
    legend: g('--chart-legend', 'rgba(255,255,255,.72)'),
    legendStrong: g('--chart-legend-strong', '#ffffff'),
    divergingStops: [
      parseHexTriple(g('--chart-diverging-low', '#8b1a1a')),
      parseHexTriple(g('--chart-diverging-mid', '#808080')),
      parseHexTriple(g('--chart-diverging-high', '#22d3ee')),
    ],
    tooltipBg: g('--chart-tooltip-bg', '#091c1e'),
    tooltipTitle: g('--chart-tooltip-title', '#ffffff'),
    tooltipBody: g('--chart-tooltip-body', 'rgba(255,255,255,.64)'),
    tooltipBorder: g('--chart-tooltip-border', 'rgba(255,255,255,.1)'),
  }
}
